import { type CSSProperties, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  financeApi, type DispatchRef, type InvoiceTraceability, type InvoiceView, type PaymentView,
} from "../../api/finance";
import { supplierFinanceApi, type InvoiceContent } from "../../api/supplierFinance";
import { myLotDocumentApi, type CustomerLotDocument, type CustomerLotDocuments } from "../../api/lotDocuments";
import { nazivVrsteDokumenta } from "../../lib/lotDocumentKind";
import { Tag } from "../../components/primitives.jsx";
import { money, fmtDate, docnjaDana } from "./money";
import { formatNumber } from "../../lib/format";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import type { ApiError } from "../../api/client";
import { reportsApi } from "../../api/reports";

/**
 * Read-only invoice detail overlay. Shows only fields the invoice object
 * actually carries (no net/VAT or line items exist on InvoiceView, and there is
 * no invoice PDF).
 *
 * Payments do not appear here. In this ERP a payment carries no invoice, so the two cannot be
 * matched: all 97.756 payments arrive without one. A paid amount printed above an empty payment list
 * would be two statements about the same money that contradict each other. The amount is right — it is
 * the invoice minus what is still open — and the absence of a list is right too, so what the panel owes
 * the reader is the reason for both. Follows the app modal idiom
 * (overlay + panel, overlay-click / Escape to close) but themed via CSS
 * variables so it also works in dark mode.
 */
export function InvoiceDetailModal({
  invoice,
  payments,
  onClose,
  strana = "kupac",
  asOf,
  onDisputesChanged,
}: {
  invoice: InvoiceView;
  payments: PaymentView[];
  onClose: () => void;
  /** The invoice was marked disputed or its mark taken off: the page behind reads its figures again. */
  onDisputesChanged?: () => void;
  /**
   * The day overdue is counted on, as on the card the invoice was opened from. The days late are
   * counted up to it, so the panel states the same figure as the row behind it.
   */
  asOf?: string | null;
  /**
   * Na kojoj je strani ovaj račun.
   *
   * Modal je zajednički za kupce i dobavljače, a poreklo robe se čita sa različitih krajnjih tačaka.
   * Dok ovoga nije bilo, račun dobavljača je zvao kupčevu tačku, dobijao 404 — njegovog id-a nema u
   * `finance_invoice` — i `catch` je to tiho progutao, pa je odeljak „Poreklo robe" prosto nestajao.
   */
  strana?: "kupac" | "dobavljac";
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const mozeTrag = hasPermission("traceability:view");
  // Dokumente o kvalitetu uz robu traži samo kupac, za svoj račun. Interne role imaju svoj registar,
  // gde se dokument i prilaže i pušta; ovde bi im bio treći spisak istih redova.
  const svojRacun = hasPermission("finance:view_own");
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const prevFocusRef = useRef<HTMLElement | null>(null);

  // Focus the close button on open, close on Escape, restore focus on unmount.
  useEffect(() => {
    prevFocusRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      prevFocusRef.current?.focus?.();
    };
  }, [onClose]);

  // Sledljivost se traži tek kad se račun otvori, a ne uz spisak: spisak od pedeset računa
  // inače vuče i otpremnice i LOT oznake koje niko nije pogledao.
  const [trag, setTrag] = useState<InvoiceTraceability | null>(null);
  // Strana dobavljača: šta je kupljeno i koji prijem je plaćen. Podatak dolazi sa dokumenta računa u
  // ERP-u, a ne iz knjige iz koje račun nastaje, pa se čita posebnom tačkom.
  const [sadrzaj, setSadrzaj] = useState<InvoiceContent | null>(null);
  useEffect(() => {
    if (strana !== "dobavljac") return;
    let otkazano = false;
    supplierFinanceApi.invoiceContent(invoice.id)
      .then((c) => { if (!otkazano) setSadrzaj(c); })
      .catch(() => { if (!otkazano) setSadrzaj({ lines: [], deliveries: [] }); });
    return () => { otkazano = true; };
  }, [invoice.id, strana]);

  useEffect(() => {
    if (strana !== "kupac") return;
    let otkazano = false;
    financeApi.traceability(invoice.id)
      .then((t) => { if (!otkazano) setTrag(t); })
      .catch(() => { if (!otkazano) setTrag({ dispatches: [], lots: [], wholeNotes: false }); });
    return () => { otkazano = true; };
  }, [invoice.id, strana]);

  // Sertifikat analize i izjava o NON GMO statusu, za LOT-ove sa ovog računa.
  //
  // Traži se samo ono što je direktor kvaliteta pustio; dokument koji nije pušten se ne vraća i kupac ne
  // zna da postoji. Prazan odgovor je uobičajen i ne piše se ništa — račun bez priloženih dokumenata nije
  // greška nego stanje.
  const [dokumenti, setDokumenti] = useState<CustomerLotDocuments[] | null>(null);
  useEffect(() => {
    if (!svojRacun) return;
    let otkazano = false;
    myLotDocumentApi.forInvoice(invoice.id)
      .then((r) => { if (!otkazano) setDokumenti(r); })
      .catch(() => { if (!otkazano) setDokumenti([]); });
    return () => { otkazano = true; };
  }, [invoice.id, svojRacun]);

  async function preuzmi(d: CustomerLotDocument) {
    try {
      const { url } = await myLotDocumentApi.downloadUrl(d.id);
      // Potpisana veza je prisilno preuzimanje (attachment), pa se fajl ne prikazuje u pretraživaču.
      window.open(url, "_blank", "noopener");
    } catch (e) {
      // Dokument je u međuvremenu povučen ili ga više nema. To se kaže, ne preskače.
      toast.error((e as ApiError).message);
    }
  }

  const cur = invoice.currency || "EUR";
  const related = payments.filter((p) => p.invoiceNumber === invoice.invoiceNumber);

  // Dospeo račun bez broja dana kaže samo da je prošlo; sedam dana i sto sedam dana nisu isti
  // razgovor sa kupcem, a razlika se inače traži u glavi od dva datuma.
  const docnja = invoice.overdue
    ? docnjaDana(invoice.dueDate, asOf ? new Date(asOf + "T00:00:00") : undefined)
    : null;
  const platio = invoice.openAmount != null && invoice.openAmount <= 0;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby={titleId} style={overlay} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        {/* Zaglavlje nosi ono zbog čega se račun otvara: koliko i da li je plaćeno. Ostalo je
            kontekst i stoji sitnije; kad je sve iste težine, ništa se ne vidi. */}
        <div style={head}>
          <div style={{ minWidth: 0 }}>
            <div style={eyebrow}>
              {vrstaDokumenta(invoice.documentType, t) ?? t("finance.invoices.number", "Faktura")}
            </div>
            <h3 id={titleId} style={titleStyle}>{invoice.invoiceNumber}</h3>
            {invoice.description && <div style={subject}>{invoice.description}</div>}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="btn ghost sm"
            aria-label={t("finance.invoices.close", "Close")}
            onClick={onClose}
            style={closeBtn}
          >
            ✕
          </button>
        </div>

        <div style={heroRed}>
          <div>
            <div style={dt}>{t("finance.invoices.gross", "Iznos")}</div>
            <div style={hero}>{money(invoice.grossAmount, cur)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            {platio ? (
              <Tag kind="ok" dot={undefined} style={undefined}>
                {t("finance.status.PAID", "Plaćeno")}
              </Tag>
            ) : invoice.overdue ? (
              <Tag kind="err" dot={undefined} style={undefined}>
                {docnja != null
                  ? t("finance.invoices.overdueDays", "Dospelo, {{count}} dana", { count: docnja })
                  : t("finance.invoices.overdue", "Dospelo")}
              </Tag>
            ) : (
              <Tag kind="neutral" dot={undefined} style={undefined}>
                {t(`finance.status.${invoice.status}`, invoice.status)}
              </Tag>
            )}
            {!platio && (
              <div style={{ ...dd, marginTop: 6 }}>
                {t("finance.invoices.open", "Otvoreno")}: <b>{money(invoice.openAmount, cur)}</b>
              </div>
            )}
          </div>
        </div>

        <dl style={grid}>
          <Podatak oznaka={t("finance.invoices.issueDate", "Izdato")} vrednost={fmtDate(invoice.issueDate)} />
          <Podatak oznaka={t("finance.invoices.dueDate", "Dospeće")} vrednost={fmtDate(invoice.dueDate)} />
          <Podatak oznaka={t("finance.invoices.paid", "Plaćeno")} vrednost={money(invoice.paidAmount, cur)} />
          {invoice.orderReference && (
            <Podatak oznaka={t("finance.invoices.orderRef", "Referenca porudžbine")}
                     vrednost={invoice.orderReference} />
          )}
        </dl>

        {/* Sporno stoji posle datuma, kao i ostali odeljci: sa svojom linijom iznad, ne uz liniju ispod iznosa. */}
        {strana === "kupac" && <SpornoRacuna invoice={invoice} onChanged={onDisputesChanged} />}

        {/* Strana dobavljača: šta je kupljeno. Dolazi sa dokumenta računa u ERP-u, jer knjiga iz koje
            račun nastaje nosi iznos ali ne i artikal. Račun za uslugu stavke nema — tamo ostaje opis
            sa samog računa, koji stoji iznad. */}
        {sadrzaj && sadrzaj.lines.length > 0 && (
          <section style={odeljak}>
            <div style={sectionLabel}>{t("supplierFinance.invoice.bought", "Šta je kupljeno")}</div>
            <table className="tbl" style={{ marginTop: 6 }}>
              <thead>
                <tr>
                  <th>{t("supplierFinance.invoice.article", "Roba")}</th>
                  <th style={{ textAlign: "right" }}>{t("supplierFinance.invoice.qty", "Količina")}</th>
                  <th style={{ textAlign: "right" }}>{t("supplierFinance.invoice.price", "Cena")}</th>
                  <th style={{ textAlign: "right" }}>{t("supplierFinance.invoice.amount", "Iznos")}</th>
                  <th>{t("supplierFinance.invoice.lot", "LOT")}</th>
                </tr>
              </thead>
              <tbody>
                {sadrzaj.lines.map((l, i) => (
                  <tr key={i}>
                    <td>{l.productName ?? "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      {l.quantity != null
                        ? `${l.quantity.toLocaleString("sr-RS")} ${l.unit ?? ""}`.trim()
                        : "—"}
                    </td>
                    <td style={{ textAlign: "right" }}>{l.unitPrice != null ? money(l.unitPrice, cur) : "—"}</td>
                    <td style={{ textAlign: "right" }}>{l.netAmount != null ? money(l.netAmount, cur) : "—"}</td>
                    <td>
                      {/* The lot opens its traceability by id, and only for whoever may see it, as on a
                          customer invoice; without that right the label stays plain text. */}
                      {l.lotDesignation && l.goodsLotId && mozeTrag ? (
                        <Link to={`/traceability?lot=${l.goodsLotId}`}
                              style={{ color: "var(--brand-primary)", textDecoration: "none" }}>
                          {l.lotDesignation}
                        </Link>
                      ) : (l.lotDesignation ?? "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Koji je prijem ovaj račun platio. Jedan račun pokriva utovarni niz a ne jedan kamion — ovaj
            sa ekrana nosio je jedanaest prijemnica. Broj koji se nije razrešio u isporuku se ipak
            prikazuje: prijem je uvezen za merkantilnu soju od 2012, a račun ume da imenuje prijem
            drugog artikla ili ranije godine, pa prećutati ga značilo bi reći da prijema nema. */}
        {sadrzaj && sadrzaj.deliveries.length > 0 && (
          <section style={odeljak}>
            <div style={sectionLabel}>
              {t("supplierFinance.invoice.intake", "Prijem koji ovaj račun pokriva")}
            </div>
            <table className="tbl" style={{ marginTop: 6 }}>
              <thead>
                <tr>
                  <th>{t("supplierFinance.invoice.receipt", "Prijemnica")}</th>
                  <th>{t("supplierFinance.invoice.receivedOn", "Primljeno")}</th>
                  <th style={{ textAlign: "right" }}>{t("supplierFinance.invoice.netKg", "Neto (kg)")}</th>
                </tr>
              </thead>
              <tbody>
                {sadrzaj.deliveries.map((d) => (
                  <tr key={d.reference}>
                    <td>
                      {d.deliveryId ? (
                        <Link to={`/procurement/deliveries/${d.deliveryId}`}
                              style={{ color: "var(--brand-primary)", textDecoration: "none" }}>
                          {d.reference}
                        </Link>
                      ) : (
                        <span title={t("supplierFinance.invoice.unresolved", "Prijem nije uvezen")}>
                          {d.reference}
                        </span>
                      )}
                    </td>
                    <td>{d.receivedOn ? fmtDate(d.receivedOn) : "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      {d.netKg != null ? d.netKg.toLocaleString("sr-RS") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Odakle je roba otišla i pod kojim LOT-om.
            Veza računa i otpremnice je zabeležena u ERP-u, a LOT stoji na svakoj stavci
            otpremnice — za razliku od stavke računa, gde ga ima na jednoj od petnaest. */}
        {trag && (trag.dispatches.length > 0 || trag.lots.length > 0) && (
          <section style={odeljak}>
            <div style={sectionLabel}>{t("finance.invoices.origin", "Poreklo robe")}</div>

            {/* One row per dispatch note: its number, its date, the loading order it was issued on and the
                order or offer behind that. A chip that carried all four on one line was unreadable. */}
            {trag.dispatches.length > 0 && (
              <table className="tbl kartice" style={{ width: "100%", marginTop: 6 }}>
                <thead>
                  <tr>
                    <th>{t("finance.invoices.colDispatch", "Otpremnica")}</th>
                    <th style={{ width: 110 }}>{t("finance.invoices.colDispatchDate", "Datum")}</th>
                    <th>{t("finance.invoices.colDispatchOrder", "Nalog za utovar")}</th>
                    <th>{t("finance.invoices.colSource", "Porudžbina")}</th>
                  </tr>
                </thead>
                <tbody>
                  {trag.dispatches.map((d) => (
                    <tr key={d.reference}>
                      <td className="card-naslov" data-label={t("finance.invoices.colDispatch", "Otpremnica")}>
                        <code style={{ fontFamily: "var(--mono)", whiteSpace: "nowrap" }}>{d.number ?? d.reference}</code>
                      </td>
                      <td data-label={t("finance.invoices.colDispatchDate", "Datum")} style={{ whiteSpace: "nowrap" }}>
                        {d.issueDate
                          ? fmtDate(d.issueDate)
                          // Oznaka koju nismo našli nije greška koju treba sakriti: račun jeste imao
                          // otpremnicu, pa se kaže i to.
                          : <span style={tiho}>{t("finance.invoices.dispatchNotFound", "nije nađena")}</span>}
                      </td>
                      <td data-label={t("finance.invoices.colDispatchOrder", "Nalog za utovar")}>
                        {d.dispatchOrderNumber
                          ? <span style={{ whiteSpace: "nowrap" }}>{d.dispatchOrderNumber}</span>
                          : <span style={tiho}>—</span>}
                      </td>
                      <td data-label={t("finance.invoices.colSource", "Porudžbina")}>
                        <IzvorOtpremnice d={d} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {trag.lots.length > 0 && (
              <table className="tbl kartice" style={{ width: "100%", marginTop: 8 }}>
                <thead>
                  <tr>
                    <th style={{ width: 150 }}>{t("finance.invoices.lots", "LOT")}</th>
                    <th>{t("finance.invoices.goods", "Roba")}</th>
                    <th style={{ textAlign: "right", width: 110 }}>{t("finance.invoices.qty", "Količina")}</th>
                    <th style={{ textAlign: "right", width: 130 }}>{t("finance.invoices.expires", "Ističe")}</th>
                  </tr>
                </thead>
                <tbody>
                  {trag.lots.map((l) => (
                    <tr key={`${l.lotId}-${l.unit ?? ""}`}>
                      <td className="card-naslov" data-label={t("finance.invoices.lots", "LOT")}>
                        {/* Oznaka vodi na sledljivost, ali samo onome ko je sme videti: korak
                            napred pokazuje i tuđe isporuke. Bez prava ostaje običan tekst. */}
                        {mozeTrag && l.designation ? (
                          <Link to={`/traceability?lot=${l.lotId}`}
                                style={{ fontFamily: "var(--mono)", color: "var(--brand-primary)" }}
                                title={t("finance.invoices.traceLot", "Poreklo i isporuke ovog LOT-a")}>
                            {l.designation}
                          </Link>
                        ) : (
                          <code style={{ fontFamily: "var(--mono)" }}>{l.designation ?? "—"}</code>
                        )}
                      </td>
                      <td data-label={t("finance.invoices.goods", "Roba")} style={{ color: "var(--ink-2)" }}>{l.productName}</td>
                      <td data-label={t("finance.invoices.qty", "Količina")}
                          style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {l.quantity == null ? "—" : (
                          <>
                            {formatNumber(l.quantity, undefined, { maximumFractionDigits: 3 })}
                            {l.unit ? ` ${l.unit}` : ""}
                          </>
                        )}
                      </td>
                      <td data-label={t("finance.invoices.expires", "Ističe")}
                          style={{ textAlign: "right", color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                        {l.expiresOn ? fmtDate(l.expiresOn) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {/* Without article lines the lots cannot be narrowed to what this invoice bills, so the
                table holds whole dispatch notes and says so. */}
            {trag.lots.length > 0 && trag.wholeNotes && (
              <p style={{ ...tiho, margin: "6px 0 0", fontSize: 12, lineHeight: 1.45 }}>
                {t("finance.invoices.wholeNotes",
                   "Račun nema stavke po artiklu, pa su navedeni LOT-ovi celih otpremnica.")}
              </p>
            )}
          </section>
        )}

        {/* Dokumenti o kvalitetu uz robu sa ovog računa.
            Sertifikat analize i izjava o NON GMO statusu idu po LOT-u, pa i ovde stoje grupisani po
            LOT-u: to je oznaka koju kupac ima na deklaraciji i po kojoj se spaja sa svojom isporukom.
            Preuzimanje je prisilno, kao i svaki drugi otpremljen fajl u portalu. */}
        {dokumenti && dokumenti.length > 0 && (
          <section style={odeljak}>
            <div style={sectionLabel}>
              {t("finance.invoices.qualityDocs", "Dokumenti o kvalitetu")}
            </div>
            {dokumenti.map((g) => (
              <div key={g.lot.id} style={{ marginTop: 8 }}>
                <div style={otpremniceRed}>
                  <code style={{ fontFamily: "var(--mono)" }}>{g.lot.designation ?? "—"}</code>
                  <span style={tiho}>{g.lot.productName ?? ""}</span>
                </div>
                <div style={{ ...otpremniceRed, marginTop: 6 }}>
                  {g.documents.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className="btn ghost sm"
                      onClick={() => void preuzmi(d)}
                      title={d.fileName}
                    >
                      {nazivVrsteDokumenta(d.kind, t)}
                      {d.issueNumber ? ` ${d.issueNumber}` : ""}
                      {d.expired ? ` · ${t("finance.invoices.docExpired", "istekao")}` : ""}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Uplate.
            U ovom ERP-u uplata ne nosi broj računa — nijedna od 97.756 — pa istorije po fakturi
            nema i ne može je biti. Odsustvo se kaže tihom napomenom uz plaćeni iznos, koji i jeste
            izveden; prazan okvir sa centriranom rečenicom na istom mestu liči na grešku. */}
        {related.length > 0 ? (
          <section style={odeljak}>
            <div style={sectionLabel}>{t("finance.invoices.paymentHistory", "Istorija plaćanja")}</div>
            <table className="tbl" style={{ marginTop: 4, width: "100%" }}>
              <thead>
                <tr>
                  <th>{t("finance.payments.date", "Datum")}</th>
                  <th style={{ textAlign: "right" }}>{t("finance.payments.amount", "Iznos")}</th>
                  <th>{t("finance.payments.method", "Način")}</th>
                  <th>{t("finance.payments.reference", "Poziv na broj")}</th>
                </tr>
              </thead>
              <tbody>
                {related.map((p) => (
                  <tr key={p.id}>
                    <td>{fmtDate(p.paymentDate)}</td>
                    <td style={{ textAlign: "right" }}>{money(p.amount, p.currency)}</td>
                    <td>{p.method ?? "—"}</td>
                    <td>{p.reference ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : (
          <p style={napomena}>
            {payments.some((p) => p.invoiceNumber)
              ? t("finance.invoices.noPayments", "Za ovu fakturu nema evidentiranih uplata.")
              : t("finance.invoices.paymentsNotLinked",
                  "Uplate iz ERP-a ne nose broj računa, pa se ne mogu vezati za pojedinačnu fakturu. Plaćeni iznos je razlika ukupnog i otvorenog iznosa.")}
          </p>
        )}
      </div>
    </div>
  );
}

/** Jedan podatak u mreži: oznaka sitno, vrednost ispod. */
function Podatak({ oznaka, vrednost }: { oznaka: string; vrednost: string }) {
  return (
    <div style={cell}>
      <dt style={dt}>{oznaka}</dt>
      <dd style={dd}>{vrednost}</dd>
    </div>
  );
}

/**
 * Sporno potraživanje na računu kupca: Bankomova oznaka da naplatu ne očekuje kako je fakturisano. Sporan račun se
 * podrazumevano ne računa u dugovanja, ni na finansijama ni u izveštaju komercijale. Označava i skida oznaku onaj ko
 * vodi registar spornog; ostali zaposleni samo vide oznaku, a kupac je ne vidi nikad.
 */
function SpornoRacuna({ invoice, onChanged }: { invoice: InvoiceView; onChanged?: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const smeDaOznaci = hasPermission("management_report:mark");
  const [sporan, setSporan] = useState(invoice.disputed ?? false);
  const [zasto, setZasto] = useState(invoice.disputeReason ?? null);
  // The reason being typed; null while the form is closed.
  const [razlog, setRazlog] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!sporan && (!smeDaOznaci || invoice.openAmount <= 0)) return null;

  async function oznaci() {
    if (razlog == null || !razlog.trim()) return;
    setBusy(true);
    try {
      await reportsApi.markInvoiceDisputed(invoice.id, razlog.trim());
      setSporan(true);
      setZasto(razlog.trim());
      setRazlog(null);
      toast.success(t("disputes.marked", "The invoice is marked disputed."));
      onChanged?.();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function skini() {
    setBusy(true);
    try {
      await reportsApi.resolveInvoiceDispute(invoice.id);
      setSporan(false);
      toast.success(t("disputes.unmarked", "The mark is taken off; the dispute stays in the register as resolved."));
      onChanged?.();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={odeljak}>
      <div style={sectionLabel}>{t("disputes.title", "Disputed receivable")}</div>
      {sporan ? (
        <div className="uz-oznake">
          <Tag kind="warn" dot={undefined} style={undefined}>{t("disputes.badge", "Disputed")}</Tag>
          <span style={{ fontSize: 13, color: "var(--ink-2)", flex: 1, minWidth: 160 }}>{zasto}</span>
          {smeDaOznaci && (
            <button type="button" className="btn sm" disabled={busy} onClick={() => void skini()}>
              {t("disputes.unmark", "Take the mark off")}
            </button>
          )}
        </div>
      ) : razlog === null ? (
        <div className="uz-oznake">
          <span style={{ fontSize: 13, color: "var(--ink-3)", flex: 1, minWidth: 160 }}>
            {t("disputes.hint", "A disputed invoice is left out of the debt on the finance screens and in the commercial report, unless disputed receivables are shown.")}
          </span>
          <button type="button" className="btn sm" onClick={() => setRazlog("")}>
            {t("disputes.mark", "Mark as disputed")}
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          <textarea
            className="input"
            rows={2}
            value={razlog}
            onChange={(e) => setRazlog(e.target.value)}
            aria-label={t("disputes.reason", "Reason")}
            placeholder={t("disputes.reasonPlaceholder", "Why it is disputed, e.g. the customer disputes the quality")}
            style={{ resize: "vertical" }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="btn" disabled={busy} onClick={() => setRazlog(null)}>
              {t("disputes.cancel", "Cancel")}
            </button>
            <button type="button" className="btn primary" disabled={busy || !razlog.trim()} onClick={() => void oznaci()}>
              {t("disputes.save", "Mark disputed")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * The order or offer a dispatch note's loading order was made from; a dash when there is none. The source opens
 * on its own page for whoever may open that page and stays plain text for anyone else.
 */
function IzvorOtpremnice({ d }: { d: DispatchRef }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  if (!d.sourceKind || !d.sourceNumber) return <span style={tiho}>—</span>;
  const ponuda = d.sourceKind === "OFFER";
  const smeIzvor = d.sourceId != null
    && hasPermission(ponuda ? "section:offers:read" : "section:orders:read");
  // The number stays on one line, so a narrow screen never splits it at its hyphen.
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      {ponuda ? `${t("finance.invoices.sourceOffer", "ponuda")} ` : ""}
      {smeIzvor ? (
        <Link to={ponuda ? `/offers/${d.sourceId}` : `/orders/${d.sourceId}`}
              style={{ color: "var(--brand-primary)", textDecoration: "none" }}>
          {d.sourceNumber}
        </Link>
      ) : d.sourceNumber}
    </span>
  );
}

/**
 * Vrsta dokumenta iz ERP-a, napisana kao reč a ne kao šifra.
 *
 * ERP je piše spojeno i bez naših slova („KonacniRacun"), što na ekranu izgleda kao mašinski
 * ispis. Ovo nije tumačenje podatka nego njegovo pisanje: nepoznata vrsta prolazi nepromenjena.
 */
function vrstaDokumenta(vrsta: string | null, t: (k: string, d: string) => string): string | null {
  if (!vrsta) return null;
  const poznate: Record<string, [string, string]> = {
    // Izlazna strana
    KonacniRacun: ["finance.docType.konacniRacun", "Konačni račun"],
    Racun: ["finance.docType.racun", "Račun"],
    InoRacun: ["finance.docType.inoRacun", "Inostrani račun"],
    PDVIzlazniRacunZaUsluge: ["finance.docType.uslugeRacun", "Račun za usluge"],
    IzlazniRacunZaUsluge: ["finance.docType.uslugeRacun", "Račun za usluge"],
    AvansniRacun: ["finance.docType.avansniRacun", "Avansni račun"],
    KonacniRacunZaHotel: ["finance.docType.hotelRacun", "Hotelski račun"],
    // Ulazna strana. `PDVUlazniRacunZaUsluge` je sam 125.724 reda od 191.470 — dve trećine svih
    // računa dobavljača — pa je bez njega zaglavlje pokazivalo mašinski zapis na većini računa.
    KonacniUlazniRacun: ["finance.docType.ulazniRacun", "Ulazni račun"],
    UlazniRacun: ["finance.docType.ulazniRacun", "Ulazni račun"],
    PDVUlazniRacunZaUsluge: ["finance.docType.ulazniUslugeRacun", "Ulazni račun za usluge"],
    UlazniRacunZaUsluge: ["finance.docType.ulazniUslugeRacun", "Ulazni račun za usluge"],
    PDVKalkulacijaUlaza: ["finance.docType.kalkulacijaUlaza", "Kalkulacija ulaza"],
    KalkulacijaUlaza: ["finance.docType.kalkulacijaUlaza", "Kalkulacija ulaza"],
    // Knjigovodstveni zapisi: nisu računi, ali stoje na istom kontu i zato dolaze na ovaj ekran.
    NalogGlavneKnjige: ["finance.docType.nalogGK", "Nalog glavne knjige"],
    PreknjizavanjeKonta: ["finance.docType.preknjizavanje", "Preknjižavanje konta"],
    KursneRazlike: ["finance.docType.kursneRazlike", "Kursne razlike"],
    FinansijskiInterniNalog: ["finance.docType.interniNalog", "Interni nalog"],
    PDVIzvestajOKnjizenju: ["finance.docType.izvestajKnjizenje", "Izveštaj o knjiženju"],
    IzvestajOKnjizenju: ["finance.docType.izvestajKnjizenje", "Izveštaj o knjiženju"],
    InterniObracunPoreskogDuznika: ["finance.docType.obracunPoreskogDuznika", "Interni obračun poreskog dužnika"],
    ZatvaranjeAvansa: ["finance.docType.zatvaranjeAvansa", "Zatvaranje avansa"],
    RasporedObaveza: ["finance.docType.rasporedObaveza", "Raspored obaveza"],
    DebitKreditNota: ["finance.docType.debitKreditNota", "Debit/kredit nota"],
    Izvod: ["finance.docType.izvod", "Izvod"],
  };
  const p = poznate[vrsta];
  return p ? t(p[0], p[1]) : vrsta;
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--overlay)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: 16,
};
const panel: CSSProperties = {
  background: "var(--surface)",
  color: "var(--ink)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  padding: 22,
  width: 560,
  maxWidth: "100%",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "var(--shadow-2)",
  display: "flex",
  flexDirection: "column",
  gap: 18,
};
const heroRed: CSSProperties = {
  display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16,
  paddingBottom: 14, borderBottom: "1px solid var(--border)",
};
const hero: CSSProperties = {
  fontSize: 26, fontWeight: 700, color: "var(--ink)", lineHeight: 1.15,
  fontVariantNumeric: "tabular-nums", marginTop: 2,
};
const odeljak: CSSProperties = {
  paddingTop: 12, borderTop: "1px solid var(--border)",
};
const otpremniceRed: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
};
const napomena: CSSProperties = {
  margin: 0, paddingTop: 12, borderTop: "1px solid var(--border)",
  fontSize: 12, color: "var(--ink-3)", lineHeight: 1.45,
};
const head: CSSProperties = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 };
const eyebrow: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5,
};
const titleStyle: CSSProperties = { margin: "2px 0 0", fontSize: 20, color: "var(--ink)", fontFamily: "var(--mono)" };
const closeBtn: CSSProperties = { padding: "4px 10px", lineHeight: 1, fontSize: 15 };
const grid: CSSProperties = {
  margin: 0,
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: "12px 18px",
};
const cell: CSSProperties = { display: "flex", flexDirection: "column", gap: 2 };
const dt: CSSProperties = { fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 };
const dd: CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink)", fontVariantNumeric: "tabular-nums" };
const tiho: CSSProperties = { color: "var(--ink-3)" };
const subject: CSSProperties = {
  margin: 0, fontSize: 14, color: "var(--ink)", lineHeight: 1.45,
};
const sectionLabel: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6,
};
